import { useEffect, useMemo, useState } from "react";

import { useFormikWithDirtyForm } from "@shared";
import { useAlertContext } from "@shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as Yup from "yup";

import { ContactDetailType, SendSmsRequest, SmsTemplateResponse, clientsApi, smsApi, smsTemplatesApi } from "@/api";
import { withLocalErrorHandling } from "@/api/middleware/errorMiddleware";
import { ApiErrorCode, getApiErrorCode } from "@/common/helper/apiErrorHelper";
import { getLocalTimeZoneId } from "@/common/utils/getLocalTimeZoneId";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

import { SmsSendFormValues } from "../SmsSend.types";

export function useSmsSend(clientId: string, onClose: () => void, initialTemplateTitle?: string) {
  const { t } = useTranslation();
  const { showSuccess, showError } = useAlertContext();
  const queryClient = useQueryClient();

  const [userSelectedTemplate, setUserSelectedTemplate] = useState<SmsTemplateResponse | null>(null);

  const { data: templates = [] } = useQuery({
    queryKey: [QueryKey.SmsTemplates],
    queryFn: () => smsTemplatesApi.getAllSmsTemplates(),
  });

  const { data: phoneOptions = [], isPending: isPhoneOptionsPending } = useQuery({
    queryKey: [QueryKey.ClientPersonalData, clientId],
    queryFn: () => clientsApi.getClientPersonalData({ clientId }),
    select: (data) =>
      data.contactDetails
        .filter((cd) => cd.type === ContactDetailType.Phone && cd.value.trim() !== "")
        .map((cd) => cd.value),
  });

  const sendSmsMutation = useMutation({
    mutationFn: (request: SendSmsRequest) => smsApi.sendSms({ clientId, sendSmsRequest: request }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.ClientCaseNotes, clientId] });
      showSuccess(t("clients.client.sms.dialog.successMessage", { recipientNumber: variables.recipientNumber }));
      onClose();
    },
    onError: () => {
      showError(t("clients.client.sms.dialog.errorMessage"));
    },
  });

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        recipientNumber: Yup.string().required(
          t("common.validation.fieldRequired", { fieldName: t("clients.client.sms.dialog.recipientNumber") })
        ),
        messageContent: Yup.string().required(
          t("common.validation.fieldRequired", { fieldName: t("clients.client.sms.dialog.messageContent") })
        ),
      }),
    [t]
  );

  const formik = useFormikWithDirtyForm<SmsSendFormValues>({
    initialValues: {
      recipientNumber: phoneOptions[0] ?? "",
      messageContent: "",
    },
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values) => {
      const request: SendSmsRequest = {
        recipientNumber: values.recipientNumber,
        messageContent: values.messageContent,
        originTimeZoneId: getLocalTimeZoneId(),
      };
      try {
        await sendSmsMutation.mutateAsync(request);
      } catch {
        // Reported by the mutation's error handler; the dialog stays open so the message is kept.
      }
    },
  });

  // When opened for a specific purpose (e.g. after activating an upload link) the template is preset
  // by title and the dropdown is disabled, so the selection is derived rather than stored in state.
  const autoTemplate = initialTemplateTitle
    ? (templates.find((template) => template.title === initialTemplateTitle) ?? null)
    : null;
  const selectedTemplate = initialTemplateTitle ? autoTemplate : userSelectedTemplate;

  const { mutate: resolveTemplate } = useMutation({
    mutationFn: (template: SmsTemplateResponse) =>
      smsTemplatesApi.resolveSmsTemplate(
        {
          id: template.id,
          resolveSmsTemplateRequest: { clientId },
        },
        // The no-active-upload-link 404 is an expected flow; onError toasts for every failure.
        withLocalErrorHandling
      ),
    onSuccess: (response, resolvedTemplate) => {
      // useMutation-level onSuccess also fires for every mutate call — ignore late results of a
      // previously selected template so they can't clobber the current selection's content.
      if (resolvedTemplate.id !== selectedTemplate?.id) {
        return;
      }

      // Apply the resolved template as the new clean baseline (not a dirty edit). Opening the dialog
      // with a preselected template would otherwise mark the form dirty with no user action, tripping
      // the unsaved-changes guard on close and the global navigation block.
      formik.resetForm({ values: { ...formik.values, messageContent: response.content } });
    },
    onError: async (error, failedTemplate) => {
      // useMutation-level onError fires for every mutate call, not just the latest — ignore late
      // failures of a previously selected template so they can't clobber the current selection.
      if (failedTemplate.id !== selectedTemplate?.id) {
        return;
      }

      if ((await getApiErrorCode(error)) === ApiErrorCode.UploadTokenNotFound) {
        // Without an active upload link the {{Link}} template stays unapplied — staff activate the
        // Kundenupload first (the preset flow after activation always has one).
        setUserSelectedTemplate(null);
        showError(t("clients.client.templates.uploadLinkRequired"));
      } else {
        showError(t("clients.client.sms.dialog.errorMessage"));
      }
    },
  });

  const applyTemplate = (template: SmsTemplateResponse | null) => {
    setUserSelectedTemplate(template);
    if (template) {
      resolveTemplate(template);
    }
  };

  // Resolve the preset template's {{Link}} once — but only after the phone query has settled, so the
  // `enableReinitialize` it triggers can't reset the message body after the resolved content is set.
  useEffect(() => {
    if (autoTemplate && !isPhoneOptionsPending) {
      resolveTemplate(autoTemplate);
    }
  }, [autoTemplate, isPhoneOptionsPending, resolveTemplate]);

  return { formik, phoneOptions, templates, applyTemplate, selectedTemplate };
}
